import { createFileRoute } from "@tanstack/react-router";

import { PageHero } from "@/components/site/PageHero";
import { SiteFooter } from "@/components/site/SiteFooter";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — adaptED" },
      {
        name: "description",
        content:
          "Read the terms and conditions for using the adaptED learning platform and services.",
      },
      { property: "og:title", content: "Terms of Service — adaptED" },
      {
        property: "og:description",
        content:
          "Terms and conditions for using the adaptED learning platform and services.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/terms" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "Terms of Service — adaptED" },
      {
        name: "twitter:description",
        content:
          "Terms and conditions for using the adaptED learning platform and services.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.adapted.info/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <main id="top">
      <PageHero
        label="Legal"
        title="Terms of Service"
        paragraph="The rules and expectations for using adaptED's platform and services."
      />

      <section className="surface-warm py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="prose prose-lg max-w-none text-warm-ink">
            <p className="text-sm text-warm-ink/60">Last updated: 15 August 2026</p>

            <h2>1. Acceptance of terms</h2>
            <p>
              By accessing or using adaptED's website, applications, learning companion or any related
              services (collectively, the "Services"), you agree to be bound by these Terms of
              Service. If you do not agree, please do not use the Services.
            </p>

            <h2>2. Who may use the Services</h2>
            <p>
              The Services are intended for learners, parents, educators and schools in South
              Africa. Users under the age of 18 must have permission from a parent or legal guardian.
              Schools and other organisations must be authorised to act on behalf of the institution
              they represent.
            </p>

            <h2>3. Accounts and security</h2>
            <p>
              You are responsible for keeping your account credentials safe and for all activity
              that occurs under your account. Please notify us immediately of any unauthorised use.
            </p>

            <h2>4. Acceptable use</h2>
            <p>
              You agree to use the Services only for lawful educational purposes. You may not misuse
              the platform, attempt to access systems you are not authorised to use, upload harmful
              content, or interfere with other users' access.
            </p>

            <h2>5. Intellectual property</h2>
            <p>
              All content, branding, software and materials provided through the Services are owned
              by adaptED or its licensors and are protected by applicable intellectual property
              laws. You may not copy, modify, distribute or resell any part of the Services without
              our written permission.
            </p>

            <h2>6. Pilot and early-access features</h2>
            <p>
              Features marked as pilot, beta or early access may be experimental and are provided "as
              is" during the testing period. We may change, suspend or discontinue pilot features at
              any time.
            </p>

            <h2>7. Termination</h2>
            <p>
              We may suspend or terminate your access if you violate these terms or if required by
              law. You may stop using the Services at any time.
            </p>

            <h2>8. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, adaptED is not liable for any indirect,
              incidental or consequential damages arising from your use of the Services. Our total
              liability is limited to the amount you paid for the Services in the 12 months before the
              claim, or R100 where no payment was made.
            </p>

            <h2>9. Governing law</h2>
            <p>
              These terms are governed by the laws of the Republic of South Africa. Any disputes will
              be resolved in the competent courts of South Africa.
            </p>

            <h2>10. Changes to these terms</h2>
            <p>
              We may update these Terms of Service from time to time. The updated version will be
              posted on this page with a revised date. Continued use of the Services after changes
              means you accept the updated terms.
            </p>

            <h2>11. Contact us</h2>
            <p>
              If you have questions about these terms, please contact us through our{" "}
              <a href="/contact" className="text-primary-strong hover:underline">
                Contact page
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
